import type { GenerationPhase } from '../ProgramEvaluation/generation-hook';
import { schoolYearStartYear } from '../ProgramEvaluation/utils/schoolYear';
import type { SampleGateState } from '../Samples/sampleGate';
import type { PeStatus } from './programEvaluationStatus';

export type PeCardView =
  | { kind: 'pending' }
  | { kind: 'unavailable' }
  | { kind: 'preview' }
  | { kind: 'generate' }
  | { kind: 'generating' }
  | { kind: 'failed' }
  | { kind: 'generated'; generatedAt: string }
  | { kind: 'stale'; generatedAt: string };

export type CardGeneration = {
  phase: GenerationPhase;
  jobId: string | null;
  jobUpdatedAt: string | null;
  pollExhausted: boolean;
};

export function programEvaluationCardView(
  gate: SampleGateState,
  status: PeStatus | null,
  generation: CardGeneration,
  statusGivenUp = false,
  dataYear: string | null = null,
): PeCardView {
  const { phase, jobId, jobUpdatedAt, pollExhausted } = generation;
  if (gate === 'absent') return { kind: 'preview' };
  if (gate !== 'present') return { kind: 'pending' };
  if (phase === 'submitting' || phase === 'generating') return { kind: 'generating' };
  if (phase === 'failed' || phase === 'error') {
    if (status?.status === 'generating' && status.jobId !== jobId) return { kind: 'generating' };
    const serverKnowsBetter = pollExhausted && status !== null && status.status !== 'none';
    if (!serverKnowsBetter && !supersededFailure(status, jobUpdatedAt)) return { kind: 'failed' };
  }
  if (status === null) return { kind: statusGivenUp ? 'unavailable' : 'pending' };
  if (status.status === 'generated') {
    const rerunnable = !dataOlderThan(dataYear, status.schoolYear);
    if (status.cannotGenerate && rerunnable) return { kind: 'failed' };
    const regenerable = status.stale && rerunnable;
    return { kind: regenerable ? 'stale' : 'generated', generatedAt: status.generatedAt ?? '' };
  }
  if (statusGivenUp) return { kind: 'unavailable' };
  if (phase === 'ready') return { kind: 'pending' };
  return { kind: status.status === 'generating' ? 'generating' : 'generate' };
}

function dataOlderThan(dataYear: string | null, artifactYear: string | null): boolean {
  if (dataYear === null || artifactYear === null) return false;
  const data = schoolYearStartYear(dataYear);
  const artifact = schoolYearStartYear(artifactYear);
  return data !== null && artifact !== null && data < artifact;
}

function supersededFailure(status: PeStatus | null, jobUpdatedAt: string | null): boolean {
  if (status?.status !== 'generated' || status.generatedAt === null || jobUpdatedAt === null) return false;
  const failedAt = Date.parse(jobUpdatedAt);
  const generatedAt = Date.parse(status.generatedAt);
  return !Number.isNaN(failedAt) && !Number.isNaN(generatedAt) && failedAt < generatedAt;
}

export function formatCardDate(iso: string): string {
  const date = new Date(iso);
  if (iso === '' || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
